import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { PRODUCT_CATEGORIES } from '../../data/store';
import { FiPlus, FiX, FiSun, FiMoon } from 'react-icons/fi';

function RoutineSection({ time, products, onAdd, onRemove }) {
  const [input, setInput] = useState('');
  const icon = time === 'AM' ? FiSun : FiMoon;

  const handleAdd = (product) => {
    const trimmed = product.trim();
    if (!trimmed || products.includes(trimmed)) return;
    onAdd(trimmed);
    setInput('');
  };

  return (
    <div className="glass-card rounded-[2rem] p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center">
          <SafeIcon icon={icon} className="text-xl text-slate-800" />
        </div>
        <h3 className="font-sans font-medium text-lg text-slate-800">{time} Routine</h3>
        <span className="ml-auto text-xs text-slate-400 font-sans font-medium bg-white/50 px-3 py-1 rounded-full">
          {products.length} items
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {PRODUCT_CATEGORIES.map(cat => (
          <button
            key={cat}
            onClick={() => handleAdd(cat)}
            className={`text-xs px-4 py-2 rounded-full font-sans font-medium transition-all active:scale-95 ${
              products.includes(cat)
                ? 'bg-slate-900 text-white'
                : 'glass-input text-slate-600'
            }`}
          >
            {products.includes(cat) ? '✓ ' : '+ '}{cat}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(input); }}
          placeholder="Add custom product…"
          className="flex-1 text-sm font-sans glass-input rounded-full px-5 py-3 focus:outline-none text-slate-800 placeholder:text-slate-400"
        />
        <button
          onClick={() => handleAdd(input)}
          className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center text-white flex-shrink-0 active:scale-95 transition-transform"
        >
          <SafeIcon icon={FiPlus} className="text-lg" />
        </button>
      </div>

      {products.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/50">
          {products.map((p, i) => (
            <div key={i} className="flex items-center gap-2 bg-white/60 rounded-full px-4 py-2 border border-white/80 shadow-sm">
              <span className="text-sm font-sans font-medium text-slate-700">{p}</span>
              <button
                onClick={() => onRemove(p)}
                className="text-slate-400 hover:text-slate-600 active:scale-90 transition-transform flex items-center"
              >
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function StepRoutine({ data, onChange }) {
  const routines = data.routines || { AM: [], PM: [] };

  const addProduct = (time, product) => {
    onChange({ routines: { ...routines, [time]: [...(routines[time] || []), product] } });
  };

  const removeProduct = (time, product) => {
    onChange({ routines: { ...routines, [time]: routines[time].filter(p => p !== product) } });
  };

  return (
    <div className="space-y-4">
      {/* Why Save a Routine? */}
      <div className="rounded-2xl px-4 py-3.5" style={{ background: 'rgba(92,138,120,0.07)', border: '1px solid rgba(92,138,120,0.2)' }}>
        <p className="font-sans text-xs font-semibold mb-1" style={{ color: '#5C8A78' }}>Why Save a Routine?</p>
        <p className="font-sans text-xs text-slate-600 leading-relaxed">
          Your routine is tracked alongside your photos so you can see what actually improves your skin over time.
        </p>
        <p className="font-sans text-xs mt-2 leading-relaxed" style={{ color: '#5C8A78' }}>
          ✦ We'll help you identify which routines lead to your best results.
        </p>
      </div>

      <p className="font-sans text-sm text-slate-500">What products did you use today?</p>
      <RoutineSection time="AM" products={routines.AM || []} onAdd={p => addProduct('AM', p)} onRemove={p => removeProduct('AM', p)} />
      <RoutineSection time="PM" products={routines.PM || []} onAdd={p => addProduct('PM', p)} onRemove={p => removeProduct('PM', p)} />
    </div>
  );
}
